/**
 * Préférences utilisateur AGORA (stockées côté serveur, synchronisées entre appareils).
 * Validées à l'identique par le front et l'API (normaliserPreferences).
 */
import type { z } from 'zod';
import type { schemaPreferences } from './schemas';
import { estLangue } from './langues';
import { FILIALES, TOUTES_FILIALES } from './filiales';
import { noeuds } from './catalogue';
import { REPARTITION_MESSAGERIE } from './outils/messagerie';

export const WIDGETS_ACCUEIL = [
  'indicateurs',        // tuiles KPI (mes tickets, OT du jour, NC à traiter…)
  'objetsRecents',      // derniers objets consultés
  'meteo',              // météo du site du jour
  'horloges',           // heure des filiales
  'utilisesRecemment',  // sous-modules récents
  'accesRapides',       // favoris / accès rapides
  'actualite',          // actualité PRODEVAL
  'linkedin',           // PRODEVAL sur LinkedIn
] as const;
export type WidgetAccueil = (typeof WIDGETS_ACCUEIL)[number];

/**
 * Modes d'affichage d'une carte (demande du 23/10/2026) : compact = uniquement la localité de l'utilisateur,
 * moyen = vue resserrée, complet = vue du design (défaut, mode absent).
 */
export const MODES_WIDGET = ['compact', 'moyen', 'complet'] as const;
export type ModeWidget = (typeof MODES_WIDGET)[number];
/** Cartes qui proposent un choix de mode. */
export const WIDGETS_A_MODES = ['meteo', 'horloges'] as const satisfies readonly WidgetAccueil[];
export type WidgetAModes = (typeof WIDGETS_A_MODES)[number];

export function aDesModes(id: WidgetAccueil): id is WidgetAModes {
  return (WIDGETS_A_MODES as readonly WidgetAccueil[]).includes(id);
}

export type Theme = 'clair' | 'sombre' | 'systeme';

/**
 * Zoom de l'affichage (demande du 30/10/2026), réglé à part sur ordinateur (« Bureau ») et sur téléphone (« Mobile »).
 * 100 % = affichage de référence d'AGORA, plus dense qu'avant : l'ancien zoom navigateur de 90 % (web/src/core/zoom.ts).
 */
export const ZOOMS = [80, 90, 100, 110, 125] as const;
export type Zoom = (typeof ZOOMS)[number];
export const ZOOM_DEFAUT: Zoom = 100;
export const estZoom = (v: unknown): v is Zoom => (ZOOMS as readonly unknown[]).includes(v);

/**
 * Préférences (type déduit du schéma Zod schemaPreferences) : langue (absente = langue de la filiale par défaut),
 * thème, zoom (Bureau, Mobile), filiale par défaut (code ou ALL), favoris du dock (5 max), cartes de l'accueil (ordre,
 * visibilité, mode d'affichage), barre latérale repliée, 8 derniers modules consultés, messagerie dans le dock (activée
 * par défaut) et répartition liste / message de son écran, softphone (téléphone du dock, désactivé par défaut), Scribe
 * (bouton de l'enregistreur de réunions dans le dock, désactivé par défaut).
 */
export type Preferences = z.infer<typeof schemaPreferences>;

export const FAVORIS_MAX = 5;
export const RECENTS_MAX = 8;

export function preferencesParDefaut(): Preferences {
  return {
    version: 1,
    theme: 'systeme',
    zoom: { bureau: ZOOM_DEFAUT, mobile: ZOOM_DEFAUT },
    favoris: [],
    accueil: { widgets: WIDGETS_ACCUEIL.map((id) => ({ id, visible: true })) },
    barreLateraleRepliee: false,
    recents: [],
    messagerie: true,
    messagerieRepartition: REPARTITION_MESSAGERIE.defaut,
    softphone: false,
    scribe: false,
  };
}

/** Nettoie une préférence reçue (valeurs inconnues ignorées, limites appliquées). */
export function normaliserPreferences(entree: unknown): Preferences {
  const base = preferencesParDefaut();
  if (!entree || typeof entree !== 'object') return base;
  const e = entree as Record<string, unknown>;
  const ids = new Set(noeuds().map((n) => n.id));
  const out: Preferences = { ...base };
  if (estLangue(e.langue)) out.langue = e.langue;
  if (e.theme === 'clair' || e.theme === 'sombre' || e.theme === 'systeme') out.theme = e.theme;
  const zoom = e.zoom as { bureau?: unknown; mobile?: unknown } | undefined;
  out.zoom = { bureau: estZoom(zoom?.bureau) ? zoom.bureau : ZOOM_DEFAUT, mobile: estZoom(zoom?.mobile) ? zoom.mobile : ZOOM_DEFAUT };
  if (typeof e.filiale === 'string' && (e.filiale === TOUTES_FILIALES || FILIALES.some((f) => f.code === e.filiale))) out.filiale = e.filiale;
  if (Array.isArray(e.favoris)) out.favoris = [...new Set(e.favoris.filter((x): x is string => typeof x === 'string' && ids.has(x)))].slice(0, FAVORIS_MAX);
  if (typeof e.barreLateraleRepliee === 'boolean') out.barreLateraleRepliee = e.barreLateraleRepliee;
  if (typeof e.messagerie === 'boolean') out.messagerie = e.messagerie;
  if (typeof e.messagerieRepartition === 'number' && Number.isFinite(e.messagerieRepartition)) {
    const { min, max } = REPARTITION_MESSAGERIE;
    out.messagerieRepartition = Math.min(max, Math.max(min, Math.round(e.messagerieRepartition)));
  }
  if (typeof e.softphone === 'boolean') out.softphone = e.softphone;
  if (typeof e.scribe === 'boolean') out.scribe = e.scribe;
  const acc = (e.accueil as { widgets?: unknown })?.widgets;
  if (Array.isArray(acc)) {
    const vus = new Set<string>();
    const liste: Preferences['accueil']['widgets'] = [];
    for (const w of acc) {
      const id = (w as { id?: unknown })?.id;
      if (typeof id === 'string' && (WIDGETS_ACCUEIL as readonly string[]).includes(id) && !vus.has(id)) {
        vus.add(id);
        const mode = (w as { mode?: unknown }).mode;
        liste.push({
          id: id as WidgetAccueil, visible: (w as { visible?: unknown }).visible !== false,
          ...(aDesModes(id as WidgetAccueil) && (MODES_WIDGET as readonly unknown[]).includes(mode) ? { mode: mode as ModeWidget } : {}),
        });
      }
    }
    for (const id of WIDGETS_ACCUEIL) if (!vus.has(id)) liste.push({ id, visible: false });
    out.accueil = { widgets: liste };
  }
  if (Array.isArray(e.recents)) {
    out.recents = e.recents
      .filter((r): r is { id: string; le: string } => !!r && typeof (r as { id?: unknown }).id === 'string' && ids.has((r as { id: string }).id) && typeof (r as { le?: unknown }).le === 'string')
      .slice(0, RECENTS_MAX);
  }
  return out;
}

/** Mode d'affichage d'une carte de l'accueil (absent = complet). */
export function modeWidget(p: Preferences, id: WidgetAModes): ModeWidget {
  return p.accueil.widgets.find((w) => w.id === id)?.mode || 'complet';
}

export function changerModeWidget(p: Preferences, id: WidgetAModes, mode: ModeWidget): Preferences {
  return { ...p, accueil: { widgets: p.accueil.widgets.map((w) => (w.id === id ? { ...w, mode } : w)) } };
}

/**
 * Nouvel ordre des cartes visibles (glisser-déposer de l'accueil) : les cartes masquées gardent leur place,
 * une carte inconnue ou masquée dans `ordreVisibles` est ignorée.
 */
export function ordonnerWidgets(p: Preferences, ordreVisibles: readonly WidgetAccueil[]): Preferences {
  const visibles = new Set(p.accueil.widgets.filter((w) => w.visible).map((w) => w.id));
  const suite = [...new Set(ordreVisibles.filter((id) => visibles.has(id)))];
  for (const w of p.accueil.widgets) if (w.visible && !suite.includes(w.id)) suite.push(w.id);
  const parId = new Map(p.accueil.widgets.map((w) => [w.id, w]));
  let i = 0;
  const widgets = p.accueil.widgets.map((w) => (w.visible ? parId.get(suite[i++])! : w));
  return { ...p, accueil: { widgets } };
}

/** Ajoute un module consulté en tête des récents. */
export function ajouterRecent(p: Preferences, id: string, le = new Date().toISOString()): Preferences {
  const recents = [{ id, le }, ...p.recents.filter((r) => r.id !== id)].slice(0, RECENTS_MAX);
  return { ...p, recents };
}
